import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
    Autocomplete, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper,
    Select, Stack, Switch, TextField, Typography
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { toast } from 'react-toastify';
import { formatCurrency, formatNumber } from '../utils/utils.ts';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import POCSVUpload from '../components/poCSVUpload';

dayjs.extend(utc);

const api = axios.create({ withCredentials: true });

// Lookup tables based on the TN API documentation
const SEATING_TYPES = [
    { id: 1, description: "Odd/Even" },
    { id: 2, description: "Consecutive" },
    { id: 3, description: "General Admission" }
];

const STOCK_TYPES = [
    { id: 1, description: "Standard" },
    { id: 2, description: "e-Ticket" },
    { id: 3, description: "Commemorative" },
    { id: 4, description: "Paperless" },
    { id: 5, description: "AXS Mobile ID" },
    { id: 7, description: "Mobile" }
];

const TICKET_GROUP_TYPES = [
    { id: 1, description: "Event Ticket" },
    { id: 2, description: "Suite" },
    { id: 3, description: "Parking" },
    { id: 4, description: "Hotel" },
    { id: 5, description: "Package" },
    { id: 6, description: "Backstage Pass" },
    { id: 7, description: "Pit Pass" },
    { id: 8, description: "VIP Pass" },
    { id: 9, description: "Meet/Greet Pass" },
    { id: 10, description: "Pass" }
];

const SHIPPING_METHODS = [
    { id: 22, description: "E-Ticket" },
    { id: 18, description: "Will Call" },
    { id: 14, description: "Local Pickup Near Venue" },
    { id: 16, description: "Courier" },
    { id: 6, description: "FedEx Priority Overnight" },
    { id: 7, description: "FedEx 2nd Day" },
    { id: 8, description: "FedEx Standard Overnight" },
    { id: 9, description: "FedEx First Overnight" },
    { id: 10, description: "FedEx Express Saver" },
    { id: 15, description: "FedEx Home/Ground Delivery" },
    { id: 19, description: "Regular Mail" },
    { id: 20, description: "Certified Mail" },
    { id: 21, description: "Express Delivery" },
    { id: 23, description: "Paperless" },
    { id: 24, description: "Electronic Transfer" },
    { id: 27, description: "UPS" }
];

const SHIPPING_METHOD_SPECIAL = [
    { id: 0, description: "Near Term Special Delivery" },
    { id: 1, description: "E-Ticket" },
    { id: 2, description: "Will Call" },
    { id: 3, description: "Local Pickup Near Venue" },
    { id: 10, description: "TN Digital Conversion" },
    { id: 13, description: "Direct Transfer" },
    { id: 14, description: "Upload QR Screenshot" },
    { id: 15, description: "AXS Mobile ID" }
];

// Default broadcast channels for ticket groups
const DEFAULT_BROADCAST_CHANNELS = "1,2,3,4,6";

function POS({ current_user, current_broker }) {
    const { getAccessTokenSilently } = useAuth0();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Event search states
    const [eventSearchTerm, setEventSearchTerm] = useState("");
    const [venueSearchTerm, setVenueSearchTerm] = useState("");
    const [eventDateStart, setEventDateStart] = useState(null);
    const [eventDateEnd, setEventDateEnd] = useState(null);
    const [eventSearchOffset, setEventSearchOffset] = useState(0);
    const [maxResults, setMaxResults] = useState(750);

    // Event search results
    const [searchingEvents, setSearchingEvents] = useState(false);
    const [eventSearchResults, setEventSearchResults] = useState([]);
    const [totalEventCount, setTotalEventCount] = useState(0);

    // Selected event and dialogs
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [poDialogOpen, setPoDialogOpen] = useState(false);
    const [ticketGroupDialogOpen, setTicketGroupDialogOpen] = useState(false);

    // Vendors for PO creation
    const [vendors, setVendors] = useState([]);
    const [loadingVendors, setLoadingVendors] = useState(false);

    // PO Form state
    const [poForm, setPoForm] = useState({
        customer_id: null,
        currency_id: 1, // Default to USD
        internal_po_notes: "",
        external_po_notes: "",
        shipping_method_id: 22, // Default to E-Ticket
        csr_id: 1, // Default to System Account
        office_id: 1,
        tm_order_id: "",
        vendor_id: current_broker !== 'all' ? current_broker : null,
        paid_in_full: false,
        payment_type_id: 2, // Default to ACH
        external_po_number: "",
        ticket_groups: []
    });

    // Current ticket group being edited
    const [currentTicketGroup, setCurrentTicketGroup] = useState({
        EventID: null,
        PosEventId: null,
        Section: "",
        Row: "",
        LowSeat: 1,
        Quantity: 2,
        FacePrice: 0,
        WholesalePrice: 0,
        RetailPrice: 0,
        TicketGroupCost: 0,
        SeatingTypeID: 2, // Default to Consecutive
        StockTypeID: 2, // Default to E-Ticket
        OnHand: true,
        OnHandDate: dayjs().format(),
        TicketGroupTypeID: 1, // Default to Event Ticket
        InternalTGNotes: "",
        ExternalTGNotes: "",
        PricingNotes: "",
        ShippingMethodSpecialID: 1, // Default to E-Ticket
        NearTermDisplayOptionID: 0,
        ExposureTypeID: 1, // Default to Show All Details
        BroadcastChannelIDs: DEFAULT_BROADCAST_CHANNELS,
        SplitRuleID: 1 // Default to All Splits
    });

    const [editingTicketGroupIndex, setEditingTicketGroupIndex] = useState(-1);

    // Ticket Series state
    const [ticketSeriesMode, setTicketSeriesMode] = useState(false);
    const [ticketSeriesDialogOpen, setTicketSeriesDialogOpen] = useState(false);
    const [selectedEvents, setSelectedEvents] = useState([]);
    const [ticketSeriesTemplate, setTicketSeriesTemplate] = useState({
        ticket_groups: []
    });
    const [currentSeriesTicketGroup, setCurrentSeriesTicketGroup] = useState({
        Section: "",
        Row: "",
        LowSeat: 1,
        Quantity: 2,
        FacePrice: 0,
        WholesalePrice: 0,
        RetailPrice: 0,
        TicketGroupCost: 0,
        SeatingTypeID: 2, // Default to Consecutive
        StockTypeID: 2, // Default to E-Ticket
        OnHand: true,
        OnHandDate: dayjs().format(),
        TicketGroupTypeID: 1, // Default to Event Ticket
        InternalTGNotes: "",
        ExternalTGNotes: "",
        PricingNotes: "",
        ShippingMethodSpecialID: 1, // Default to E-Ticket
        NearTermDisplayOptionID: 0,
        ExposureTypeID: 1, // Default to Show All Details
        BroadcastChannelIDs: DEFAULT_BROADCAST_CHANNELS,
        SplitRuleID: 1 // Default to All Splits
    });
    const [editingSeriesTicketGroupIndex, setEditingSeriesTicketGroupIndex] = useState(-1);
    const [seriesTicketGroupDialogOpen, setSeriesTicketGroupDialogOpen] = useState(false);

    // CSV Upload state
    const [csvUploadDialogOpen, setCsvUploadDialogOpen] = useState(false);

    // Function to reset ticket group form to default values
    const resetTicketGroupForm = () => {
        setCurrentTicketGroup({
            EventID: null,
            PosEventId: null,
            Section: "",
            Row: "",
            LowSeat: 1,
            Quantity: 2,
            FacePrice: 0,
            WholesalePrice: 0,
            RetailPrice: 0,
            TicketGroupCost: 0,
            SeatingTypeID: 2, // Default to Consecutive
            StockTypeID: 2, // Default to E-Ticket
            OnHand: true,
            OnHandDate: dayjs().format(),
            TicketGroupTypeID: 1, // Default to Event Ticket
            InternalTGNotes: "",
            ExternalTGNotes: "",
            PricingNotes: "",
            ShippingMethodSpecialID: 1, // Default to E-Ticket
            NearTermDisplayOptionID: 0,
            ExposureTypeID: 1, // Default to Show All Details
            BroadcastChannelIDs: DEFAULT_BROADCAST_CHANNELS,
            SplitRuleID: 1 // Default to All Splits
        });
        setEditingTicketGroupIndex(-1);
    };

    const resetSeriesDialogForm = () => {
        setTicketSeriesTemplate({
            ticket_groups: []
        });
        resetSeriesTicketGroupForm();
        setSeriesTicketGroupDialogOpen(false);
        // Reset PO form to defaults for series
        setPoForm({
            customer_id: null,
            currency_id: 1,
            internal_po_notes: "",
            external_po_notes: "",
            shipping_method_id: 22,
            csr_id: 1,
            office_id: 1,
            tm_order_id: "",
            vendor_id: current_broker !== 'all' ? current_broker : null,
            paid_in_full: false,
            payment_type_id: 2,
            external_po_number: "",
            ticket_groups: []
        });
    };

    const exitSeriesMode = () => {
        resetSeriesDialogForm();
        setSelectedEvents([]);
        setTicketSeriesMode(false);
    };

    // Ticket Series Functions
    const handleSeriesTicketGroupChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Mirrors Retail Price to wholesale price
        if (name == "RetailPrice") {
            currentSeriesTicketGroup.RetailPrice = value;
            currentSeriesTicketGroup.WholesalePrice = value;
            setCurrentSeriesTicketGroup({...currentSeriesTicketGroup});
        }
        else {
            setCurrentSeriesTicketGroup({
                ...currentSeriesTicketGroup,
                [name]: type === 'checkbox' ? checked : value
            });
        }
    };

    const resetSeriesTicketGroupForm = () => {
        setCurrentSeriesTicketGroup({
            Section: "",
            Row: "",
            LowSeat: 1,
            Quantity: 2,
            FacePrice: 0,
            WholesalePrice: 0,
            RetailPrice: 0,
            TicketGroupCost: 0,
            SeatingTypeID: 2,
            StockTypeID: 2,
            OnHand: true,
            OnHandDate: dayjs().format(),
            TicketGroupTypeID: 1,
            InternalTGNotes: "",
            ExternalTGNotes: "",
            PricingNotes: "",
            ShippingMethodSpecialID: 1,
            NearTermDisplayOptionID: 0,
            ExposureTypeID: 1,
            BroadcastChannelIDs: DEFAULT_BROADCAST_CHANNELS,
            SplitRuleID: 1
        });
        setEditingSeriesTicketGroupIndex(-1);
    };

    const handleAddSeriesTicketGroup = () => {
        resetSeriesTicketGroupForm();
        setSeriesTicketGroupDialogOpen(true);
    };

    const handleEditSeriesTicketGroup = (index) => {
        setCurrentSeriesTicketGroup(ticketSeriesTemplate.ticket_groups[index]);
        setEditingSeriesTicketGroupIndex(index);
        setSeriesTicketGroupDialogOpen(true);
    };

    const handleDeleteSeriesTicketGroup = (index) => {
        const updatedTicketGroups = [...ticketSeriesTemplate.ticket_groups];
        updatedTicketGroups.splice(index, 1);
        setTicketSeriesTemplate({
            ...ticketSeriesTemplate,
            ticket_groups: updatedTicketGroups
        });
    };

    const handleSaveSeriesTicketGroup = () => {
        // Validate ticket group
        if (!currentSeriesTicketGroup.Section) {
            toast.error("Section is required");
            return;
        }

        if (!currentSeriesTicketGroup.Row) {
            toast.error("Row is required");
            return;
        }

        if (!currentSeriesTicketGroup.LowSeat) {
            toast.error("Low Seat is required");
            return;
        }

        if (!currentSeriesTicketGroup.Quantity || currentSeriesTicketGroup.Quantity < 1) {
            toast.error("Quantity must be at least 1");
            return;
        }

        // Update or add ticket group
        const updatedTicketGroups = [...ticketSeriesTemplate.ticket_groups];

        if (editingSeriesTicketGroupIndex >= 0) {
            updatedTicketGroups[editingSeriesTicketGroupIndex] = currentSeriesTicketGroup;
        } else {
            updatedTicketGroups.push(currentSeriesTicketGroup);
        }

        setTicketSeriesTemplate({
            ...ticketSeriesTemplate,
            ticket_groups: updatedTicketGroups
        });

        resetSeriesTicketGroupForm();
        setSeriesTicketGroupDialogOpen(false);
    };

    const handleEventSelection = (eventIds) => {
        const events = eventSearchResults.filter(event => eventIds.includes(event.EventID));

        // Validate that all selected events are from the same venue
        if (events.length > 1) {
            const firstVenue = events[0].VenueName;
            const allSameVenue = events.every(event => event.VenueName === firstVenue);

            if (!allSameVenue) {
                toast.error("All selected events must be from the same venue");
                return false;
            }
        }

        setSelectedEvents(events);
        return true;
    };

    const handleCreateTicketSeries = () => {
        if (selectedEvents.length === 0) {
            toast.error("Please select at least one event");
            return;
        }

        if (!poForm.vendor_id) {
            toast.error("Please select a vendor");
            return;
        }

        if (ticketSeriesTemplate.ticket_groups.length === 0) {
            toast.error("Please add at least one ticket group");
            return;
        }

        // Create individual POs for each selected event
        createSeriesPurchaseOrders();
    };

    const createSeriesPurchaseOrders = async () => {
        setSubmitting(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            for (const event of selectedEvents) {
                try {
                    // Create ticket groups for this event with EventID populated
                    const ticketGroups = ticketSeriesTemplate.ticket_groups.map(group => ({
                        ...group,
                        EventID: event.EventID
                    }));

                    // Prepare the data according to the API structure
                    const purchaseOrderData = {
                        customer_id: poForm.customer_id,
                        currency_id: poForm.currency_id || 1,
                        internal_notes: poForm.internal_po_notes,
                        external_notes: poForm.external_po_notes,
                        shipping_method_id: poForm.shipping_method_id,
                        csr_id: poForm.csr_id,
                        office_id: poForm.office_id,
                        tm_order_id: poForm.tm_order_id,
                        vendor_id: poForm.vendor_id,
                        paid_in_full: poForm.paid_in_full,
                        payment_type_id: poForm.payment_type_id,
                        system_credit_card_id: poForm.system_credit_card_id,
                        external_po_number: poForm.external_po_number || 'n/a',
                        mercury_transaction_id: poForm.mercury_transaction_id,
                        ticket_groups: ticketGroups,
                        user: current_user
                    };

                    const response = await api.post(
                        `${process.env.REACT_APP_BASEURL}/api/tn/purchase-order`,
                        purchaseOrderData,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );

                    if (response.data && response.data.Success) {
                        successCount++;
                    } else {
                        errorCount++;
                        console.error(`Error creating PO for event ${event.EventName}:`, response.data.Message);
                    }
                } catch (error) {
                    errorCount++;
                    console.error(`Error creating PO for event ${event.EventName}:`, error);
                }
            }

            // Show results
            if (successCount > 0 && errorCount === 0) {
                toast.success(`Successfully created ${successCount} purchase orders`);
            } else if (successCount > 0 && errorCount > 0) {
                toast.warning(`Created ${successCount} purchase orders, ${errorCount} failed`);
            } else {
                toast.error("Failed to create any purchase orders");
            }

            // Close dialog and reset form, but keep series mode active
            if (successCount > 0) {
                setTicketSeriesDialogOpen(false);
                resetSeriesDialogForm();
            }
        } catch (error) {
            console.error("Error in createSeriesPurchaseOrders:", error);
            toast.error("Error creating purchase orders");
        } finally {
            setSubmitting(false);
        }
    };

    // Load vendors when component mounts
    useEffect(() => {
        if (current_broker) {
            fetchVendors();
        }
    }, [current_broker]);

    // Event search columns configuration
    const eventColumns = [
        { field: 'id', headerName: 'Event ID', width: 100, type: 'string' },
        { field: 'name', headerName: 'Event Name', flex: 0.3, minWidth: 180, type: 'string' },
        { field: 'date', headerName: 'Date', flex: 0.2, minWidth: 120, type: 'dateTime', },
        { field: 'venue', headerName: 'Venue', flex: 0.2, minWidth: 150, type: 'string' },
        { field: 'city', headerName: 'City', flex: 0.1, minWidth: 120, type: 'string' },
        { field: 'state', headerName: 'State', flex: 0.1, minWidth: 120, type: 'string' }
    ];

    // Ticket groups columns configuration
    const ticketGroupColumns = [
        { field: 'section', headerName: 'Section', flex: 0.15, minWidth: 100, type: 'string' },
        { field: 'seatRow', headerName: 'Row', flex: 0.1, minWidth: 80, type: 'string' },
        {
            field: 'lowSeat', headerName: 'Low Seat', flex: 0.15, minWidth: 100, type: 'number',
            renderCell: (params) => { return `${formatNumber(params.value)}` }
        },
        {
            field: 'seatingType', headerName: 'Seating Type', flex: 0.2, minWidth: 120, type: 'string',
            renderCell: (params) => {
                const seatingType = SEATING_TYPES.find(type => type.id === params.value);
                return seatingType ? seatingType.description : '';
            }
        },
        {
            field: 'quantity', headerName: 'Quantity', flex: 0.1, minWidth: 80, type: 'number',
            renderCell: (params) => { return `${formatNumber(params.value)}` }
        },
        {
            field: 'facePrice', headerName: 'Face Price', flex: 0.15, minWidth: 100, type: 'number',
            renderCell: (params) => { return `${formatCurrency(params.value)}` }
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditTicketGroup(params.row.id)}
                        title="Edit"
                    >
                        <EditIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteTicketGroup(params.row.id)}
                        title="Delete"
                    >
                        <DeleteIcon />
                    </IconButton>
                </Stack>
            )
        }
    ];

    // Fetch vendors from the API
    const fetchVendors = async () => {
        if (!current_broker) return;

        setLoadingVendors(true);

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            const response = await api.post(
                `${process.env.REACT_APP_BASEURL}/api/dashboard/customers`,
                { company: [current_broker] },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data && Array.isArray(response.data)) {
                setVendors(response.data);
                console.log("Vendors loaded:", response.data);
            }
        } catch (error) {
            console.error("Error fetching vendors:", error);
            toast.error("Unable to load vendors");
        } finally {
            setLoadingVendors(false);
        }
    };

    // Handle form input changes for PO form
    const handlePoFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setPoForm({
            ...poForm,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    // Handle form input changes for ticket group form
    const handleTicketGroupFormChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Mirrors Retail Price to wholesale price
        if (name == "RetailPrice") {
            currentTicketGroup.RetailPrice = value;
            currentTicketGroup.WholesalePrice = value;
            setCurrentTicketGroup({...currentTicketGroup});
        }
        else {
            setCurrentTicketGroup({
                ...currentTicketGroup,
                [name]: type === 'checkbox' ? checked : value
            });
        }
    };

    // Search for events
    const searchEvents = async () => {
        // Validate search criteria
        if (!eventSearchTerm && !venueSearchTerm && !eventDateStart && !eventDateEnd) {
            toast.warning("Please provide at least one search criteria");
            return;
        }

        setSearchingEvents(true);

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            // Build query parameters
            let params = new URLSearchParams();

            if (eventSearchTerm) {
                params.append('eventName', `*${eventSearchTerm}*`);
            }

            if (venueSearchTerm) {
                params.append('venueName', `*${venueSearchTerm}*`);
            }

            if (eventDateStart) {
                params.append('eventStartDateTime', eventDateStart.format());
                if (!eventDateEnd) {
                    params.append('eventEndDateTime', eventDateStart.add(36500, 'day').format());
                }
            }

            if (eventDateEnd) {
                params.append('eventEndDateTime', eventDateEnd.format());
                if (!eventDateStart) {
                    params.append('eventStartDateTime', eventDateEnd.subtract(36500, 'day').format());
                }
            }

            if (eventSearchOffset > 0) {
                params.append('offset', eventSearchOffset);
            }

            if (maxResults !== 750) {
                params.append('maxResults', maxResults);
            }

            params.append('getTotalCount', true);

            // Make the API call
            const response = await api.get(
                `${process.env.REACT_APP_BASEURL}/api/tn/events/search?${params.toString()}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data && response.data.Success) {
                if (response.data.Events && response.data.Events.length > 0) {
                    setEventSearchResults(response.data.Events);
                    if (response.data.TotalEventCount) {
                        setTotalEventCount(response.data.TotalEventCount);
                    }
                    toast.success(`Found ${response.data.Events.length} events`);
                } else {
                    setEventSearchResults([]);
                    toast.info("No events found matching your criteria");
                }
            } else {
                toast.error(response.data.Message || "Error searching for events");
                setEventSearchResults([]);
            }
        } catch (error) {
            console.error("Error searching events:", error);
            toast.error("Error searching for events");
            setEventSearchResults([]);
        } finally {
            setSearchingEvents(false);
        }
    };

    // Handle event selection from search results
    const handleEventRowClick = (params) => {
        const event = eventSearchResults.find(e => e.EventID === params.row.id);
        if (event) {
            setSelectedEvent(event);
            // Skip event details and go straight to PO creation
            // Pre-fill PO form with selected event and current broker
            setPoForm({
                ...poForm,
                vendor_id: current_broker !== 'all' ? current_broker : null,
                ticket_groups: []
            });
            setPoDialogOpen(true);
        }
    };

    // Open PO creation dialog
    const handleCreatePO = () => {
        if (!selectedEvent) {
            toast.error("No event selected");
            return;
        }

        if (!current_broker) {
            toast.error("No broker selected");
            return;
        }

        // Pre-fill PO form with selected event and current broker
        setPoForm({
            ...poForm,
            vendor_id: current_broker !== 'all' ? current_broker : null
        });

        // Initialize ticket groups array
        setPoForm(prev => ({
            ...prev,
            vendor_id: current_broker !== 'all' ? current_broker : null,
            ticket_groups: []
        }));

        setPoDialogOpen(true);
    };

    // Add a new ticket group
    const handleAddTicketGroup = () => {
        setCurrentTicketGroup({
            ...currentTicketGroup,
            EventID: selectedEvent.EventID
        });
        setEditingTicketGroupIndex(-1);
        setTicketGroupDialogOpen(true);
    };

    // Edit existing ticket group
    const handleEditTicketGroup = (index) => {
        setCurrentTicketGroup(poForm.ticket_groups[index]);
        setEditingTicketGroupIndex(index);
        setTicketGroupDialogOpen(true);
    };

    // Delete ticket group
    const handleDeleteTicketGroup = (index) => {
        const updatedTicketGroups = [...poForm.ticket_groups];
        updatedTicketGroups.splice(index, 1);
        setPoForm({
            ...poForm,
            ticket_groups: updatedTicketGroups
        });
    };

    // Save ticket group to form
    const handleSaveTicketGroup = () => {
        // Validate ticket group
        if (!currentTicketGroup.Section) {
            toast.error("Section is required");
            return;
        }

        if (!currentTicketGroup.Row) {
            toast.error("Row is required");
            return;
        }

        if (!currentTicketGroup.LowSeat) {
            toast.error("Low seat is required");
            return;
        }

        if (!currentTicketGroup.Quantity || currentTicketGroup.Quantity < 1) {
            toast.error("Quantity must be at least 1");
            return;
        }

        // Update or add ticket group
        const updatedTicketGroups = [...poForm.ticket_groups];

        if (editingTicketGroupIndex >= 0) {
            updatedTicketGroups[editingTicketGroupIndex] = currentTicketGroup;
        } else {
            updatedTicketGroups.push(currentTicketGroup);
        }

        setPoForm({
            ...poForm,
            ticket_groups: updatedTicketGroups
        });

        resetTicketGroupForm();
        setTicketGroupDialogOpen(false);
    };

    // Submit PO creation
    const handleSubmitPO = async () => {
        // Validate PO form
        if (!poForm.vendor_id) {
            toast.error("Please select a vendor");
            return;
        }

        if (poForm.ticket_groups.length === 0) {
            toast.error("Please add at least one ticket group");
            return;
        }

        setSubmitting(true);

        try {
            const token = await getAccessTokenSilently({
                audience: process.env.REACT_APP_AUTH0AUDIENCE,
                scope: process.env.REACT_APP_AUTH0SCOPE
            });

            // Prepare the data according to the API structure expected in tn.route.js
            const purchaseOrderData = {
                customer_id: poForm.customer_id,
                currency_id: poForm.currency_id || 1,
                internal_notes: poForm.internal_po_notes,
                external_notes: poForm.external_po_notes,
                shipping_method_id: poForm.shipping_method_id,
                csr_id: poForm.csr_id,
                office_id: poForm.office_id,
                tm_order_id: poForm.tm_order_id,
                vendor_id: poForm.vendor_id,
                paid_in_full: poForm.paid_in_full,
                payment_type_id: poForm.payment_type_id,
                system_credit_card_id: poForm.system_credit_card_id,
                external_po_number: poForm.external_po_number || 'n/a',
                mercury_transaction_id: poForm.mercury_transaction_id,
                ticket_groups: poForm.ticket_groups,
                user: current_user
            };

            const response = await api.post(
                `${process.env.REACT_APP_BASEURL}/api/tn/purchase-order`,
                purchaseOrderData,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data && response.data.Success) {
                toast.success("Purchase order created successfully");
                setPoDialogOpen(false);
                // Reset form
                setPoForm({
                    customer_id: null,
                    currency_id: 1,
                    internal_po_notes: "",
                    external_po_notes: "",
                    shipping_method_id: 22,
                    csr_id: 1,
                    office_id: 1,
                    tm_order_id: "",
                    vendor_id: current_broker !== 'all' ? current_broker : null,
                    paid_in_full: false,
                    payment_type_id: 2,
                    external_po_number: "",
                    ticket_groups: []
                });
            } else {
                toast.error(response.data.Message || "Error creating purchase order");
            }
        } catch (error) {
            console.error("Error creating purchase order:", error);
            toast.error("Error creating purchase order");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            {/* Event Search */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Event Name"
                        value={eventSearchTerm}
                        onChange={(e) => setEventSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                searchEvents();
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        fullWidth
                        label="Venue Name"
                        value={venueSearchTerm}
                        onChange={(e) => setVenueSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                searchEvents();
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Event Start Date"
                            value={eventDateStart}
                            onChange={setEventDateStart}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    onKeyPress: (e) => {
                                        if (e.key === 'Enter') {
                                            searchEvents();
                                        }
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            label="Event End Date"
                            value={eventDateEnd}
                            onChange={setEventDateEnd}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    onKeyPress: (e) => {
                                        if (e.key === 'Enter') {
                                            searchEvents();
                                        }
                                    }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: 'right' }}>
                    <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => setCsvUploadDialogOpen(true)}
                        sx={{ mr: 1 }}
                    >
                        CSV Upload
                    </Button>
                    <Button
                        variant={ticketSeriesMode ? "contained" : "outlined"}
                        onClick={() => {
                            setTicketSeriesMode(!ticketSeriesMode);
                            setSelectedEvents([]);
                        }}
                        sx={{ mr: 1 }}
                        color={ticketSeriesMode ? "secondary" : "primary"}
                    >
                        {ticketSeriesMode ? 'Exit Series Mode' : 'Ticket Series Mode'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setEventSearchTerm("");
                            setVenueSearchTerm("");
                            setEventDateStart(null);
                            setEventDateEnd(null);
                            setEventSearchResults([]);
                            setSelectedEvents([]);
                        }}
                        sx={{ mr: 1 }}
                    >
                        Clear Search
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={searchEvents}
                        disabled={searchingEvents}
                        startIcon={searchingEvents ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                        {searchingEvents ? 'Searching...' : 'Search Events'}
                    </Button>
                    {ticketSeriesMode && selectedEvents.length > 0 && (
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                setTicketSeriesDialogOpen(true);
                            }}
                            sx={{ ml: 1 }}
                        >
                            Create Series ({selectedEvents.length} events)
                        </Button>
                    )}
                </Grid>
            </Grid>
            <br />
            {/* Event Search Results */}
            {eventSearchResults.length > 0 && (
                <Paper elevation={1}>
                    {ticketSeriesMode && (
                        <Box sx={{ p: 2, bgcolor: 'secondary.light', color: 'secondary.contrastText' }}>
                            <Typography variant="body2">
                                <strong>Ticket Series Mode:</strong> Select multiple events from the same venue to create identical ticket inventory for each event.
                                {selectedEvents.length > 0 && ` Selected: ${selectedEvents.length} events`}
                            </Typography>
                        </Box>
                    )}
                    <div style={{ height: '600px', width: '100%' }}>
                        <DataGridPro
                            rows={eventSearchResults.map(event => ({
                                id: event.EventID,
                                name: event.EventName,
                                date: dayjs(event.EventDateAndTime).toDate(),
                                venue: event.VenueName,
                                city: event.VenueCity,
                                state: event.VenueState
                            }))}
                            columns={eventColumns}
                            onRowClick={ticketSeriesMode ? undefined : handleEventRowClick}
                            loading={searchingEvents}
                            checkboxSelection={ticketSeriesMode}
                            disableRowSelectionOnClick={ticketSeriesMode}
                            onRowSelectionModelChange={(newSelection) => {
                                if (ticketSeriesMode) {
                                    handleEventSelection(newSelection);
                                }
                            }}
                            rowSelectionModel={ticketSeriesMode ? selectedEvents.map(e => e.EventID) : []}
                            slots={{
                                toolbar: GridToolbar
                            }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 }
                                }
                            }}
                            pagination
                        />
                    </div>
                </Paper>
            )}

            {/* PO Creation Dialog */}
            <Dialog
                open={poDialogOpen}
                onClose={() => setPoDialogOpen(false)}
                maxWidth="xl"
                fullWidth
            >
                {selectedEvent && (
                    <>
                        <DialogTitle>
                            Create Purchase Order - {selectedEvent.EventName}
                            <IconButton
                                aria-label="close"
                                onClick={() => setPoDialogOpen(false)}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 8
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers>
                            <Grid container spacing={3}>
                                {/* Event Info Section */}
                                <Grid item xs={12}>
                                    <Paper elevation={2} sx={{ p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} md={8}>
                                                <Typography variant="h6">
                                                    {selectedEvent.EventName}
                                                </Typography>
                                                <Typography variant="subtitle1">
                                                    {dayjs(selectedEvent.EventDateAndTime).format('MM/DD/YYYY h:mm A')}
                                                </Typography>
                                                <Typography variant="body1">
                                                    {selectedEvent.VenueName} - {selectedEvent.VenueCity}, {selectedEvent.VenueState}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} md={4} sx={{ textAlign: { md: 'right' } }}>
                                                <Typography variant="body2">
                                                    <strong>Event ID:</strong> {selectedEvent.EventID}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>POS Event ID:</strong> {selectedEvent.PosEventID || 'N/A'}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* PO Form */}
                                <Grid item xs={12}>
                                    <Paper elevation={2} sx={{ p: 2 }}>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Purchase Order Details</Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    options={vendors}
                                                    getOptionLabel={(option) => option._id.bought_from_name}
                                                    getOptionKey={(option) => option._id.buyer_broker_id}
                                                    defaultValue={vendors.length === 1 ? vendors[0] : null}
                                                    onChange={(event, newValue) => {
                                                        setPoForm({
                                                            ...poForm,
                                                            vendor_id: newValue ? newValue._id.buyer_broker_id : null
                                                        });
                                                    }}
                                                    loading={loadingVendors}
                                                    disableClearable
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            label="Vendor Search"
                                                        />
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <FormControl fullWidth>
                                                    <InputLabel>Shipping Method</InputLabel>
                                                    <Select
                                                        name="shipping_method_id"
                                                        value={poForm.shipping_method_id || 22}
                                                        onChange={handlePoFormChange}
                                                        label="Shipping Method"
                                                    >
                                                        {SHIPPING_METHODS.map(method => (
                                                            <MenuItem key={method.id} value={method.id}>
                                                                {method.description}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField
                                                    fullWidth
                                                    label="External PO Number"
                                                    name="external_po_number"
                                                    value={poForm.external_po_number}
                                                    onChange={handlePoFormChange}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Grid>

                                {/* Ticket Groups */}
                                <Grid item xs={12}>
                                    <Paper elevation={2} sx={{ p: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6">Ticket Groups</Typography>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddTicketGroup}
                                            >
                                                Add Ticket Group
                                            </Button>
                                        </Box>

                                        {poForm.ticket_groups.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                                                No ticket groups added. Click "Add Ticket Group" to add tickets.
                                            </Typography>
                                        ) : (
                                            <DataGridPro
                                                rows={poForm.ticket_groups.map((group, index) => ({
                                                    id: index,
                                                    section: group.Section,
                                                    seatRow: group.Row,
                                                    lowSeat: group.LowSeat,
                                                    seatingType: group.SeatingTypeID,
                                                    quantity: group.Quantity,
                                                    facePrice: group.FacePrice
                                                }))}
                                                columns={ticketGroupColumns}
                                                autoHeight
                                                disableRowSelectionOnClick
                                                slots={{
                                                    toolbar: GridToolbar
                                                }}
                                                slotProps={{
                                                    toolbar: {
                                                        showQuickFilter: true,
                                                        quickFilterProps: { debounceMs: 500 }
                                                    }
                                                }}
                                                pagination
                                            />
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setPoDialogOpen(false)} color="primary">
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmitPO}
                                variant="contained"
                                color="primary"
                                disabled={submitting || poForm.ticket_groups.length === 0}
                                startIcon={submitting ? <CircularProgress size={20} /> : null}
                            >
                                {submitting ? 'Creating PO...' : 'Create Purchase Order'}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Ticket Group Dialog */}
            <Dialog
                open={ticketGroupDialogOpen}
                onClose={() => {
                    resetTicketGroupForm();
                    setTicketGroupDialogOpen(false);
                }}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {editingTicketGroupIndex >= 0 ? 'Edit Ticket Group' : 'Add Ticket Group'}
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            resetTicketGroupForm();
                            setTicketGroupDialogOpen(false);
                        }}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Section"
                                name="Section"
                                value={currentTicketGroup.Section}
                                onChange={handleTicketGroupFormChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Row"
                                name="Row"
                                value={currentTicketGroup.Row}
                                onChange={handleTicketGroupFormChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Low Seat"
                                name="LowSeat"
                                value={currentTicketGroup.LowSeat}
                                onChange={handleTicketGroupFormChange}
                                type="number"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Quantity"
                                name="Quantity"
                                value={currentTicketGroup.Quantity}
                                onChange={handleTicketGroupFormChange}
                                type="number"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Face Price"
                                name="FacePrice"
                                value={currentTicketGroup.FacePrice}
                                onChange={handleTicketGroupFormChange}
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Retail Price"
                                name="RetailPrice"
                                value={currentTicketGroup.RetailPrice}
                                onChange={handleTicketGroupFormChange}
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Wholesale Price"
                                name="WholesalePrice"
                                value={currentTicketGroup.RetailPrice}
                                onChange={handleTicketGroupFormChange}
                                disabled={true}
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                                helperText="Mirrors retail price"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Ticket Group Cost"
                                name="TicketGroupCost"
                                value={currentTicketGroup.TicketGroupCost}
                                onChange={handleTicketGroupFormChange}
                                type="number"
                                required
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                                helperText="Cost at the ticket group level"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Seating Type</InputLabel>
                                <Select
                                    name="SeatingTypeID"
                                    value={currentTicketGroup.SeatingTypeID}
                                    onChange={handleTicketGroupFormChange}
                                    label="Seating Type"
                                >
                                    {SEATING_TYPES.map(type => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Stock Type</InputLabel>
                                <Select
                                    name="StockTypeID"
                                    value={currentTicketGroup.StockTypeID}
                                    onChange={handleTicketGroupFormChange}
                                    label="Stock Type"
                                >
                                    {STOCK_TYPES.map(type => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={currentTicketGroup.OnHand}
                                        onChange={(e) => setCurrentTicketGroup({
                                            ...currentTicketGroup,
                                            OnHand: e.target.checked,
                                            OnHandDate: e.target.checked ? dayjs().format() : currentTicketGroup.OnHandDate
                                        })}
                                        name="OnHand"
                                    />
                                }
                                label="On Hand"
                                sx={{ mt: 1 }}
                            />
                        </Grid>
                        {!currentTicketGroup.OnHand && (
                            <Grid item xs={12} md={4}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="On Hand Date"
                                        value={dayjs(currentTicketGroup.OnHandDate)}
                                        onChange={(newValue) => {
                                            setCurrentTicketGroup({
                                                ...currentTicketGroup,
                                                OnHandDate: newValue ? newValue.format() : dayjs().format()
                                            });
                                        }}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                        )}
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Ticket Group Type</InputLabel>
                                <Select
                                    name="TicketGroupTypeID"
                                    value={currentTicketGroup.TicketGroupTypeID}
                                    onChange={handleTicketGroupFormChange}
                                    label="Ticket Group Type"
                                >
                                    {TICKET_GROUP_TYPES.map(type => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Shipping Method Special</InputLabel>
                                <Select
                                    name="ShippingMethodSpecialID"
                                    value={currentTicketGroup.ShippingMethodSpecialID}
                                    onChange={handleTicketGroupFormChange}
                                    label="Shipping Method Special"
                                >
                                    {SHIPPING_METHOD_SPECIAL.map(method => (
                                        <MenuItem key={method.id} value={method.id}>
                                            {method.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Internal Notes"
                                name="InternalTGNotes"
                                value={currentTicketGroup.InternalTGNotes}
                                onChange={handleTicketGroupFormChange}
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        resetTicketGroupForm();
                        setTicketGroupDialogOpen(false);
                    }} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveTicketGroup} variant="contained" color="primary">
                        Save Ticket Group
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Ticket Series Dialog */}
            <Dialog
                open={ticketSeriesDialogOpen}
                onClose={() => {
                    resetSeriesDialogForm();
                    setTicketSeriesDialogOpen(false);
                }}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Create Ticket Series
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            resetSeriesDialogForm();
                            setTicketSeriesDialogOpen(false);
                        }}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {/* Selected Events Summary */}
                    {selectedEvents.length > 0 && (
                        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Selected Events ({selectedEvents.length})
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Venue: {selectedEvents[0]?.VenueName} - {selectedEvents[0]?.VenueCity}, {selectedEvents[0]?.VenueState}
                                {ticketSeriesTemplate.ticket_groups.length > 0 && (
                                    <span> • {ticketSeriesTemplate.ticket_groups.length} ticket group{ticketSeriesTemplate.ticket_groups.length !== 1 ? 's' : ''} configured</span>
                                )}
                            </Typography>
                            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                {selectedEvents.map((event, index) => (
                                    <Typography key={event.EventID} variant="body2" sx={{ py: 0.5 }}>
                                        {index + 1}. {event.EventName} - {dayjs(event.EventDateAndTime).format('MM/DD/YYYY h:mm A')}
                                    </Typography>
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {/* Vendor Selection */}
                    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2 }}>Purchase Order Details</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Autocomplete
                                    options={vendors}
                                    getOptionLabel={(option) => option._id.bought_from_name}
                                    getOptionKey={(option) => option._id.buyer_broker_id}
                                    defaultValue={vendors.length === 1 ? vendors[0] : null}
                                    onChange={(event, newValue) => {
                                        setPoForm({
                                            ...poForm,
                                            vendor_id: newValue ? newValue._id.buyer_broker_id : null
                                        });
                                    }}
                                    loading={loadingVendors}
                                    disableClearable
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Vendor Search"
                                        />
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Shipping Method</InputLabel>
                                    <Select
                                        name="shipping_method_id"
                                        value={poForm.shipping_method_id || 22}
                                        onChange={handlePoFormChange}
                                        label="Shipping Method"
                                    >
                                        {SHIPPING_METHODS.map(method => (
                                            <MenuItem key={method.id} value={method.id}>
                                                {method.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="External PO Number"
                                    name="external_po_number"
                                    value={poForm.external_po_number}
                                    onChange={handlePoFormChange}
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Ticket Groups Management */}
                    <Paper elevation={2} sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Ticket Groups (Applied to All Events)</Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={handleAddSeriesTicketGroup}
                                size="small"
                            >
                                Add Ticket Group
                            </Button>
                        </Box>

                        {ticketSeriesTemplate.ticket_groups.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                                No ticket groups added yet. Click "Add Ticket Group" to get started.
                            </Typography>
                        ) : (
                            <Box>
                                {ticketSeriesTemplate.ticket_groups.map((group, index) => {
                                    const seatingType = SEATING_TYPES.find(type => type.id === group.SeatingTypeID);
                                    const stockType = STOCK_TYPES.find(type => type.id === group.StockTypeID);

                                    return (
                                        <Paper key={index} elevation={1} sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
                                            <Grid container spacing={2} alignItems="center">
                                                <Grid item xs={12} sm={8}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                                        Section {group.Section}, Row {group.Row}, Seats {group.LowSeat}-{group.LowSeat + group.Quantity - 1}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {group.Quantity} tickets • ${group.FacePrice} face • {seatingType?.description} • {stockType?.description}
                                                    </Typography>
                                                    {group.InternalTGNotes && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            Notes: {group.InternalTGNotes}
                                                        </Typography>
                                                    )}
                                                </Grid>
                                                <Grid item xs={12} sm={4}>
                                                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditSeriesTicketGroup(index)}
                                                            color="primary"
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteSeriesTicketGroup(index)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        )}
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        resetSeriesDialogForm();
                        setTicketSeriesDialogOpen(false);
                    }} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateTicketSeries}
                        variant="contained"
                        color="primary"
                        disabled={submitting || selectedEvents.length === 0 || !poForm.vendor_id || ticketSeriesTemplate.ticket_groups.length === 0}
                        startIcon={submitting ? <CircularProgress size={20} /> : null}
                    >
                        {submitting ? 'Creating Purchase Orders...' : `Create ${selectedEvents.length} Purchase Orders`}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Series Ticket Group Dialog */}
            <Dialog
                open={seriesTicketGroupDialogOpen}
                onClose={() => setSeriesTicketGroupDialogOpen(false)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    {editingSeriesTicketGroupIndex >= 0 ? 'Edit' : 'Add'} Ticket Group
                    <IconButton
                        aria-label="close"
                        onClick={() => setSeriesTicketGroupDialogOpen(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Section"
                                name="Section"
                                value={currentSeriesTicketGroup.Section}
                                onChange={handleSeriesTicketGroupChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Row"
                                name="Row"
                                value={currentSeriesTicketGroup.Row}
                                onChange={handleSeriesTicketGroupChange}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Low Seat"
                                name="LowSeat"
                                value={currentSeriesTicketGroup.LowSeat}
                                onChange={handleSeriesTicketGroupChange}
                                type="number"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Quantity"
                                name="Quantity"
                                value={currentSeriesTicketGroup.Quantity}
                                onChange={handleSeriesTicketGroupChange}
                                type="number"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Face Price"
                                name="FacePrice"
                                value={currentSeriesTicketGroup.FacePrice}
                                onChange={handleSeriesTicketGroupChange}
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Retail Price"
                                name="RetailPrice"
                                value={currentSeriesTicketGroup.RetailPrice}
                                onChange={handleSeriesTicketGroupChange}
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                label="Wholesale Price"
                                name="WholesalePrice"
                                value={currentSeriesTicketGroup.RetailPrice}
                                onChange={handleSeriesTicketGroupChange}
                                disabled={true}
                                type="number"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                                helperText="Mirrors retail price"
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Seating Type</InputLabel>
                                <Select
                                    name="SeatingTypeID"
                                    value={currentSeriesTicketGroup.SeatingTypeID}
                                    onChange={handleSeriesTicketGroupChange}
                                    label="Seating Type"
                                >
                                    {SEATING_TYPES.map(type => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Stock Type</InputLabel>
                                <Select
                                    name="StockTypeID"
                                    value={currentSeriesTicketGroup.StockTypeID}
                                    onChange={handleSeriesTicketGroupChange}
                                    label="Stock Type"
                                >
                                    {STOCK_TYPES.map(type => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Ticket Group Type</InputLabel>
                                <Select
                                    name="TicketGroupTypeID"
                                    value={currentSeriesTicketGroup.TicketGroupTypeID}
                                    onChange={handleSeriesTicketGroupChange}
                                    label="Ticket Group Type"
                                >
                                    {TICKET_GROUP_TYPES.map(type => (
                                        <MenuItem key={type.id} value={type.id}>
                                            {type.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Shipping Method Special</InputLabel>
                                <Select
                                    name="ShippingMethodSpecialID"
                                    value={currentSeriesTicketGroup.ShippingMethodSpecialID}
                                    onChange={handleSeriesTicketGroupChange}
                                    label="Shipping Method Special"
                                >
                                    {SHIPPING_METHOD_SPECIAL.map(method => (
                                        <MenuItem key={method.id} value={method.id}>
                                            {method.description}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Internal Notes"
                                name="InternalTGNotes"
                                value={currentSeriesTicketGroup.InternalTGNotes}
                                onChange={handleSeriesTicketGroupChange}
                                multiline
                                rows={3}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSeriesTicketGroupDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSaveSeriesTicketGroup} variant="contained" color="primary">
                        {editingSeriesTicketGroupIndex >= 0 ? 'Update' : 'Add'} Ticket Group
                    </Button>
                </DialogActions>
            </Dialog>

            {/* CSV Upload Dialog */}
            <POCSVUpload
                open={csvUploadDialogOpen}
                onClose={() => setCsvUploadDialogOpen(false)}
                current_broker={current_broker}
                current_user={current_user}
            />
        </Box>
    );
}

export default POS;